import { ReactElement, useState } from "react";
import { numericFormatter } from "react-number-format";
import { useNavigate } from "react-router-dom";
import { ContactType, FindAllContactsFilters, FindContactsDatabaseResponse } from "../../../../lib/filters/dto";
import { keycloak } from "../../../../lib/keycloak";
import { useFindAllPreferencesQuery, useUpdatePreferencesMutation } from "../../../../lib/preferences/api";
import { companyColumns, PreferencesSection, toVerifyCompanyColumns } from "../../../../lib/preferences/dto";
import { Area, DatabaseSection, TargetSection } from "../../../../lib/utils";
import { colors } from "../../../../ui/colors";
import { EditIcon } from "../../../../ui/icons/edit";
import { GlobeIcon } from "../../../../ui/icons/globe";
import { TrashIcon } from "../../../../ui/icons/trash";
import { UsersIcon } from "../../../../ui/icons/users";
import Button from "../../../../ui/molecules/button";
import Checkbox from "../../../../ui/molecules/checkbox";
import Pills from "../../../../ui/molecules/pills";
import { Dropdown } from "../../../../ui/organisms/dropdown";
import { CustomTable } from "../../../../ui/organisms/table";
import { DeleteContact } from "../../commons/delete";
import { CompaniesTabValue } from "../editCompany";

interface Props {
    filters: FindAllContactsFilters
    updateFilters: (value: Partial<FindAllContactsFilters>) => void
    contacts: FindContactsDatabaseResponse<ContactType.COMPANY>
    section: DatabaseSection | TargetSection
    selectedIds: string[]
    setSelectedIds: (ids: string[]) => void
}
export function CompaniesTable(props: Props) {
    const navigate = useNavigate()
    const { filters, updateFilters, contacts, section, selectedIds, setSelectedIds } = props
    const [showDeleteCompaniesModal, setShowDeleteCompaniesModal] = useState(false)
    const { data: preferencesResponse } = useFindAllPreferencesQuery(keycloak.subject || "", {
        skip: !keycloak.subject,
    });

    const navigatePath = new Map<DatabaseSection, string>([
        ['database', "/database/companies"],
        ['verify-list', "/verify-bins/companies"],
        ['black-list', "/black-list/companies"],
    ]);

    const [updatePreferences] = useUpdatePreferencesMutation();

    const getColumnWidth = (columnName: string, defaultWidth: number = 150, isTargetList = false): number => {
        const preferences = section === "verify-list"
            ? preferencesResponse?.data[0]?.toVerifyBinCompanyPreferences
            : preferencesResponse?.data[0]?.companyPreferences;
        return preferences?.find(col => col.column === columnName)?.width ?? defaultWidth;
    };
    return (
        <><CustomTable
            asc={filters.order}
            settedOrder={filters.sort}
            tableHeight={section === "verify-list" ? "calc(100% - 48px - 68px -  41px)" : "calc(100% - 48px - 68px)"}
            prevOrderHeads={[
                <th className="text-left w-10">
                    <Checkbox
                        shape={"square"}
                        checked={contacts.data.every((company) => selectedIds.includes(company.id))!}
                        onCheck={function (value: boolean): void {
                            if (contacts.data
                                .every((company) => selectedIds.includes(company.id))) {
                                let newSelected = [...selectedIds];
                                contacts.data.forEach((company) => {
                                    newSelected = newSelected.filter(
                                        (id) => company.id !== id
                                    );
                                });
                                setSelectedIds([...newSelected]);
                            } else {
                                setSelectedIds([
                                    ...selectedIds,
                                    ...contacts.data
                                        .filter(
                                            (company) => !selectedIds.includes(company.id)
                                        )
                                        .map((company) => company.id)!,
                                ]);
                            }
                        }} />
                </th>
            ]}
            postOrderHeads={[<th className="text-left"></th>]}
            preferences={props.section === "verify-list" ?
                preferencesResponse?.data[0]?.[PreferencesSection.toVerifyBinCompanyPreferences]?.map(pref => ({ ...pref, order: toVerifyCompanyColumns.find(col => col.column === pref.column)?.order })) ?? [] :
                preferencesResponse?.data[0]?.[PreferencesSection.companyPreferences]?.map(pref => ({ ...pref, order: companyColumns.find(col => col.column === pref.column)?.order })) ?? []}
            setPreferences={(items) => {
                if (preferencesResponse?.data[0]) {
                    updatePreferences({
                        data: {
                            owner: preferencesResponse.data[0].owner!,
                            personPreferences: preferencesResponse.data[0].personPreferences!,
                            companyPreferences: props.section === "verify-list" ? preferencesResponse.data[0].companyPreferences! : items,
                            targetListPreferences: preferencesResponse.data[0].targetListPreferences!,
                            targetListCompanyPreferences: preferencesResponse.data[0].targetListCompanyPreferences!,
                            targetListPersonPreferences: preferencesResponse.data[0].targetListPersonPreferences!,
                            toVerifyBinCompanyPreferences: props.section === "verify-list" ? items : preferencesResponse.data[0].toVerifyBinCompanyPreferences!,
                            toVerifyBinPersonPreferences: preferencesResponse.data[0].toVerifyBinPersonPreferences!,
                        },
                        id: preferencesResponse.data[0].id!,
                    });
                }
            }}
            setOrderFilters={(order) => {
                updateFilters({ order: !filters.order, sort: order });
            }}
            rows={contacts.data.map(d => ({
                key: d.id, data: [
                    {
                        item: <td className="text-text-sm font-semibold text-neutral-600">
                            <Checkbox
                                shape={"square"}
                                checked={selectedIds.includes(d.id)}
                                onCheck={function (value: boolean): void {
                                    if (selectedIds.includes(d.id)) {
                                        setSelectedIds([
                                            ...selectedIds.filter((id) => id !== d.id),
                                        ]);
                                    } else {
                                        setSelectedIds([...selectedIds, d.id]);
                                    }
                                }} />
                        </td>,
                        group: 'preSort',
                        key: 'checkbox'
                    },
                    {
                        item: <td>
                            <div
                                onClick={() => {
                                    navigate(`${navigatePath.get(section as DatabaseSection)}/${d.id}/${CompaniesTabValue.form}`);
                                }}
                                className="text-text-sm crop-text cursor-pointer font-semibold text-brandPrimary-500" style={{ width: getColumnWidth('Azienda', 200) }}>
                                {d.name}
                            </div>
                        </td>,
                        group: 'sort',
                        key: 'Azienda'
                    },
                    {
                        item: <td>
                            <div
                                className="text-text-sm text-neutral-600 crop-text"
                                style={{ width: getColumnWidth('Cod. ATECO', 150) }}
                            >
                                {d.ateco && d.ateco.atecoCode ? d.ateco.atecoCode : <Pills size={"xs"} label={"-"} outline={true} emphasis={false} color={"red"} />}
                            </div>
                        </td>,
                        group: 'sort',
                        key: 'Cod. ATECO'
                    },
                    {
                        item: <td>
                            <div
                                className="text-text-sm text-neutral-600 crop-text"
                                style={{ width: getColumnWidth('Fatturato', 120) }}
                            >
                                {d.revenue !== null ? numericFormatter(d.revenue.toString(), { thousandSeparator: '.', decimalSeparator: ',', fixedDecimalScale: true, decimalScale: 2, prefix: '€ ' }) : <Pills size={"xs"} label={"-"} outline={true} emphasis={false} color={"red"} />}
                            </div>
                        </td>,
                        group: 'sort',
                        key: 'Fatturato'
                    },
                    {
                        item: <td>
                            <div
                                className="text-text-sm text-red-700 font-semibold crop-text"
                                style={{ width: getColumnWidth('Esito', 120) }}
                            >
                                {d.toVerifyOutcomes !== null ? d.toVerifyOutcomes.join(', ') : "-"}
                            </div>
                        </td>,
                        group: 'sort',
                        key: 'Esito'
                    },
                    {
                        item: <td>
                            <a
                                rel="noreferrer"
                                target="_blank"
                                href={'tel:' + (d.phones && d.phones[0]?.phone ? d.phones[0]?.phone : '')}
                            >
                                <div className="crop-text text-text-sm text-neutral-600" style={{ width: getColumnWidth('Telefono', 150) }}>
                                    {d.phones && d.phones[0] ? (d.phones[0].phone + (d.phones.length > 1 ? (', ..., + ' + (d.phones.length - 1)) : '')) : '-'}
                                </div>
                            </a>
                        </td>,
                        group: 'sort',
                        key: 'Telefono'
                    },
                    {
                        item: <td>
                            <a
                                rel="noreferrer"
                                target="_blank"
                                href={'mailto:' + (d.emails)}
                            >
                                <div className="crop-text text-text-sm text-neutral-600" style={{ width: getColumnWidth('Email', 200) }}>
                                    {d.emails && d.emails[0] ? (d.emails[0] + (d.emails.length > 1 ? (', ..., + ' + (d.emails.length - 1)) : '')) : <Pills size={"xs"} label={"-"} outline={true} emphasis={false} color={"red"} />}
                                </div>
                            </a>
                        </td>,
                        group: 'sort',
                        key: 'Email'
                    },
                    {
                        item: <td>
                            <div
                                className="text-text-sm text-neutral-600 crop-text"
                                style={{ width: getColumnWidth('Sede azienda', 200) }}
                            >
                                {buildAddress(d.address, d.city, d.countryRegion, d.state, d.zipCode, Area.DATABASE)}
                            </div>
                        </td>,
                        group: 'sort',
                        key: 'Sede azienda'
                    },
                    {
                        item: <td>
                            <div
                                className="text-text-sm text-neutral-600 crop-text"
                                style={{ width: getColumnWidth('P. IVA', 100) }}
                            >
                                {d.vat ?? <Pills size={"xs"} label={"-"} outline={true} emphasis={false} color={"red"} />}
                            </div>
                        </td>,
                        group: 'sort',
                        key: 'P. IVA'
                    },
                    {
                        item: <td className=" relative group">
                            {d.particularities && d.particularities.length > 0 ? (
                                <div className="text-text-sm font-semibold text-neutral-600 crop-text" style={{ width: getColumnWidth('Particolarità', 100, true) }}>
                                    {d.particularities ? (d.particularities[0] + (d.particularities.length > 1 ? (', ..., + ' + (d.particularities.length - 1)) : '')) : '-'}
                                </div>
                            ) : (
                                "-"
                            )}
                            {d.particularities && d.particularities.length > 0 && <div className="absolute left-0 bottom-full z-50 hidden group-hover:block text-neutral-700 bg-neutral-200 rounded-md p-1 text-text-xs">
                                {d.particularities.map((item, index) => (
                                    <span key={index}>
                                        {item}
                                        <br />
                                    </span>
                                ))}
                            </div>}
                        </td>,
                        group: 'sort',
                        key: 'Particolarità'
                    },
                    {
                        item: <td>
                            <div
                                className="text-text-sm text-neutral-600 crop-text"
                                style={{ width: getColumnWidth('Settore', 150) }}
                            >
                                {d.ateco && d.ateco.sector ? d.ateco.sector : <Pills size={"xs"} label={"-"} outline={true} emphasis={false} color={"red"} />}
                            </div>
                        </td>,
                        group: 'sort',
                        key: 'Settore'
                    },
                    {
                        item: <td>
                            <div
                                className="text-text-sm text-neutral-600 crop-text"
                                style={{ width: getColumnWidth('Categoria', 150) }}
                            >
                                {d.ateco && d.ateco.category ? d.ateco.category : <Pills size={"xs"} label={"-"} outline={true} emphasis={false} color={"red"} />}
                            </div>
                        </td>,
                        group: 'sort',
                        key: 'Categoria'
                    },
                    {
                        item: <td>
                            <div>
                                <Pills
                                    size={"xs"}
                                    label={d.active ? "Attivo" : "Inattivo"}
                                    outline={false}
                                    emphasis={false}
                                    color={d.active ? "green" : "blue"} />
                            </div>
                        </td>,
                        group: 'sort',
                        key: 'Stato'
                    },
                    {
                        item: <td>
                            <div
                                className="text-text-sm text-neutral-600 crop-text"
                                style={{ width: getColumnWidth('Dipendenti', 100) }}
                            >
                                {d.employeesNumber !== null ? numericFormatter(d.employeesNumber.toString(), { thousandSeparator: '.' }) : <Pills size={"xs"} label={"-"} outline={true} emphasis={false} color={"red"} />}
                            </div>
                        </td>,
                        group: 'sort',
                        key: 'Dipendenti'
                    },
                    {
                        item: <td>
                            <a rel="noreferrer" target="_blank" href={d.website} className="flex items-center justify-center"><GlobeIcon color={colors.neutral[600]} size={20} /></a>
                        </td>,
                        group: 'sort',
                        key: 'Sito web'
                    },
                    {
                        item: <td className="">
                            <div className="flex items-center justify-center gap-2">
                                <div className="relative">
                                    {d.employeesId && d.employeesId.length > 0 &&
                                        <div className="h-4 min-w-4 rounded-full px-2 bg-brandPrimary-500 absolute left-1/2 text-label-xs flex items-center justify-center text-white">
                                            {d.employeesId.length}
                                        </div>}

                                    <Button
                                        icon={<UsersIcon color={""} size={0} />}
                                        size={"md"}
                                        iconPosition={"only"}
                                        disabled={!d.employeesId || d.employeesId.length === 0}
                                        variant={"link"}
                                        color={"gray"}
                                        onClick={() => {
                                            navigate(`${navigatePath.get(section as DatabaseSection)}/${d.id}/${CompaniesTabValue.employees}`);
                                        }} />
                                </div>
                                <Dropdown
                                    items={[
                                        {
                                            label: "Modifica",
                                            icon: <EditIcon color={""} size={0} />,
                                            onClick: () => {
                                                navigate(`${navigatePath.get(section as DatabaseSection)}/${d.id}/${CompaniesTabValue.form}`);
                                            },
                                        },
                                        window.location.pathname.includes('black-list') || keycloak.hasRealmRole('commercial') ?
                                            null :
                                            {
                                                label: "Cancella",
                                                icon: <TrashIcon color={""} size={0} />,
                                                onClick: () => {
                                                    setSelectedIds([d.id]);
                                                    setShowDeleteCompaniesModal(true);
                                                },
                                            },
                                    ]} />
                            </div>
                        </td>,
                        group: 'postSort',
                        key: 'menu'
                    }
                ]
            })) ?? []}
        />
            <DeleteContact
                contactType={ContactType.COMPANY}
                showModal={showDeleteCompaniesModal}
                setShowModal={setShowDeleteCompaniesModal}
                selectedIds={selectedIds}
                setSelectedIds={setSelectedIds}
            />
        </>
    )
}

export const buildAddress = (address?: string, city?: string, region?: string, state?: string, zipCode?: string | number, area?: Area): string | ReactElement => {
    let location = ''
    if (address) {
        location = address
    }
    if (city) {
        if (location !== '') {
            location = location + ', '
        }
        location = location + city
    }
    if (region) {
        if (location !== '') {
            location = location + ', '
        }
        location = location + region
    }
    if (state) {
        if (location !== '') {
            location = location + ', '
        }
        location = location + state
    }
    if (zipCode) {
        if (location !== '') {
            location = location + ', '
        }
        location = location + zipCode
    }
    return location === '' ? area === Area.DATABASE ? <Pills size={"xs"} label={"-"} outline={true} emphasis={false} color={"red"} /> : '-' : location
}
